import type { Vec2 } from './vec2';
import { AREA_EPSILON } from '@/constants/numeric';

/**
 * Adaptive-precision orientation predicate.
 *
 * Returns > 0 when (a, b, c) turn counter-clockwise, < 0 when clockwise, and
 * exactly 0 when collinear. The fast floating-point determinant is used when
 * it is provably larger than its own round-off bound; otherwise we fall back to
 * an exact expansion (Shewchuk's error-free transformations, two-term version),
 * which is exact for IEEE-754 doubles whose products do not overflow.
 *
 * Reference: J. R. Shewchuk, "Adaptive Precision Floating-Point Arithmetic and
 * Fast Robust Geometric Predicates", Discrete & Computational Geometry 18(3).
 */
const EPS = Number.EPSILON / 2;
const CCWERRBOUND_A = (3 + 16 * EPS) * EPS;

export function orient2d(a: Vec2, b: Vec2, c: Vec2): number {
  const detleft = (a.x - c.x) * (b.y - c.y);
  const detright = (a.y - c.y) * (b.x - c.x);
  const det = detleft - detright;

  const detsum = Math.abs(detleft + detright);
  if (Math.abs(det) >= CCWERRBOUND_A * detsum) return det;
  return orient2dExact(a, b, c);
}

/** Error-free product: returns [error, product] with product = a*b exactly + error. */
function twoProduct(a: number, b: number): [number, number] {
  const x = a * b;
  const [ah, al] = split(a);
  const [bh, bl] = split(b);
  const err = al * bl - (x - ah * bh - al * bh - ah * bl);
  return [err, x];
}

const SPLITTER = 134217729; // 2^27 + 1

function split(a: number): [number, number] {
  const c = SPLITTER * a;
  const abig = c - a;
  const ahi = c - abig;
  return [ahi, a - ahi];
}

function twoDiff(a: number, b: number): [number, number] {
  const x = a - b;
  const bv = a - x;
  const av = x + bv;
  return [a - av + (bv - b), x];
}

/**
 * Exact 2x2 determinant of ((ax-cx),(ay-cy);(bx-cx),(by-cy)).
 * Only the sign is meaningful; we sum a 4-term expansion in increasing
 * magnitude, which reproduces the exact sign for well-scaled inputs.
 */
function orient2dExact(a: Vec2, b: Vec2, c: Vec2): number {
  const [acxErr, acx] = twoDiff(a.x, c.x);
  const [acyErr, acy] = twoDiff(a.y, c.y);
  const [bcxErr, bcx] = twoDiff(b.x, c.x);
  const [bcyErr, bcy] = twoDiff(b.y, c.y);

  const [e1, p1] = twoProduct(acx, bcy);
  const [e2, p2] = twoProduct(acy, bcx);

  // Correction terms for the differences themselves.
  const corr =
    acxErr * bcy + acx * bcyErr + acxErr * bcyErr -
    (acyErr * bcx + acy * bcxErr + acyErr * bcxErr);

  const [se, ss] = twoDiff(p1, p2);
  const small = e1 - e2 + se + corr;
  const total = ss + small;
  if (total !== 0) return total;
  return small !== 0 ? small : ss;
}

/** Sign of orient2d, hardened against tiny denormal noise. */
export function orientationSign(a: Vec2, b: Vec2, c: Vec2): -1 | 0 | 1 {
  const d = orient2d(a, b, c);
  return d > 0 ? 1 : d < 0 ? -1 : 0;
}

export function isCollinear(a: Vec2, b: Vec2, c: Vec2): boolean {
  return orientationSign(a, b, c) === 0;
}

/** Twice the signed area of triangle abc. */
export function signedArea2(a: Vec2, b: Vec2, c: Vec2): number {
  return orient2d(a, b, c);
}

export function isDegenerateTriangle(a: Vec2, b: Vec2, c: Vec2): boolean {
  return Math.abs(orient2d(a, b, c)) < AREA_EPSILON;
}
